import React, { useMemo, useState } from "react";
import {
    Button,
    Form,
    Input,
    Modal,
    Select,
    Space,
    Typography,
    message,
} from "antd";
import { useIntl } from "react-intl";
import { SegmentBuilder } from "./SegmentBuilder";
import {
    ICreateSavedSegmentDto,
    ISavedSegment,
    ISegmentFilterCriterion,
    ISegmentSurveyCriteria,
    ISegmentationField,
    ISurveyCohort,
    MatchLogic,
} from "../../services/nestjs-base-api";

const { Title, Paragraph } = Typography;

// Stable palette used as auto-assignment source and as the picker options.
// Matches the Trends chart palette so legend colours line up across views.
export const SEGMENT_COLOURS = [
    "#1677ff",
    "#ff825c",
    "#52c41a",
    "#722ed1",
    "#faad14",
    "#13c2c2",
    "#eb2f96",
    "#fa541c",
];

export const pickNextSegmentColour = (used: Array<string | null | undefined>): string => {
    const taken = new Set(used.filter((c): c is string => !!c));
    return SEGMENT_COLOURS.find((c) => !taken.has(c)) ?? SEGMENT_COLOURS[0];
};

export interface SegmentEditorModalProps {
    open: boolean;
    initial: ISavedSegment | null;
    fields: ISegmentationField[];
    cohorts: ISurveyCohort[];
    countries: string[];
    existingColours: Array<string | null>;
    saving: boolean;
    onClose: () => void;
    onSubmit: (payload: ICreateSavedSegmentDto, id: number | null) => Promise<void>;
}

export const SegmentEditorModal: React.FC<SegmentEditorModalProps> = ({
    open,
    initial,
    fields,
    cohorts,
    countries,
    existingColours,
    saving,
    onClose,
    onSubmit,
}) => {
    const intl = useIntl();
    const [form] = Form.useForm();
    const [criteria, setCriteria] = useState<ISegmentFilterCriterion[]>([]);
    const [matchLogic, setMatchLogic] = useState<MatchLogic>("all");
    const [selectedCohortIds, setSelectedCohortIds] = useState<number[]>([]);
    const [selectedCountryCodes, setSelectedCountryCodes] = useState<string[]>([]);
    const [color, setColor] = useState<string>(SEGMENT_COLOURS[0]);

    const showCohortPicker = cohorts.length >= 2;
    const showCountryPicker = countries.length >= 2;
    const showSurveySection = showCohortPicker || showCountryPicker;

    const cohortOptions = useMemo(() => {
        const systemCohorts = cohorts.filter((c) => c.cohortType === "system");
        const customCohorts = cohorts.filter((c) => c.cohortType !== "system");
        const groups = [] as { label: string; options: { value: number; label: string }[] }[];
        if (systemCohorts.length > 0) {
            groups.push({
                label: intl.formatMessage({ id: "segments.editor.cohortGroup.builtIn", defaultMessage: "Built-in" }),
                options: systemCohorts.map((c) => ({ value: c.id, label: c.displayName ?? c.name })),
            });
        }
        if (customCohorts.length > 0) {
            groups.push({
                label: intl.formatMessage({ id: "segments.editor.cohortGroup.custom", defaultMessage: "Custom" }),
                options: customCohorts.map((c) => ({ value: c.id, label: c.displayName ?? c.name })),
            });
        }
        return groups;
    }, [cohorts, intl]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        if (!open) return;
        if (initial) {
            form.setFieldsValue({
                name: initial.name,
                description: initial.description ?? "",
            });
            setCriteria(initial.filterCriteria);
            setMatchLogic(initial.matchLogic);
            setSelectedCohortIds(initial.surveyCriteria?.cohortIds ?? []);
            setSelectedCountryCodes(initial.surveyCriteria?.countryCodes ?? []);
            setColor(initial.color ?? SEGMENT_COLOURS[0]);
        } else {
            form.resetFields();
            setCriteria([]);
            setMatchLogic("all");
            setSelectedCohortIds([]);
            setSelectedCountryCodes([]);
            setColor(pickNextSegmentColour(existingColours));
        }
    }, [open, initial?.id]);

    const handleFinish = async (values: { name: string; description?: string }) => {
        const surveyCount = selectedCohortIds.length + selectedCountryCodes.length;
        if (criteria.length === 0 && surveyCount === 0) {
            message.error(intl.formatMessage({
                id: "segments.editor.errorEmpty",
                defaultMessage: "Add at least one criterion (survey-side or customer-side)",
            }));
            return;
        }
        const surveyCriteria: ISegmentSurveyCriteria | undefined =
            surveyCount > 0
                ? {
                      ...(selectedCohortIds.length > 0 ? { cohortIds: selectedCohortIds } : {}),
                      ...(selectedCountryCodes.length > 0 ? { countryCodes: selectedCountryCodes } : {}),
                  }
                : undefined;
        const payload: ICreateSavedSegmentDto = {
            name: values.name,
            description: values.description || undefined,
            filterCriteria: criteria,
            matchLogic,
            surveyCriteria,
            color,
        };
        await onSubmit(payload, initial?.id ?? null);
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={820}
            title={
                initial
                    ? intl.formatMessage({ id: "segments.editor.editTitle", defaultMessage: "Edit segment" })
                    : intl.formatMessage({ id: "segments.editor.newTitle", defaultMessage: "New segment" })
            }
        >
            <Form form={form} layout="vertical" onFinish={handleFinish}>
                <Form.Item
                    name="name"
                    label={intl.formatMessage({ id: "segments.editor.name", defaultMessage: "Name" })}
                    rules={[{ required: true, message: intl.formatMessage({ id: "segments.editor.nameRequired", defaultMessage: "Name is required" }) }]}
                >
                    <Input placeholder="e.g. High-value subscribers" />
                </Form.Item>
                <Form.Item
                    name="description"
                    label={intl.formatMessage({ id: "segments.editor.description", defaultMessage: "Description" })}
                >
                    <Input.TextArea rows={2} />
                </Form.Item>

                <Form.Item
                    label={intl.formatMessage({ id: "segments.editor.colour", defaultMessage: "Chart colour" })}
                    tooltip={intl.formatMessage({
                        id: "segments.editor.colourTooltip",
                        defaultMessage: "Used as the segment's line colour when comparing on Trends.",
                    })}
                >
                    <Space size={8} wrap>
                        {SEGMENT_COLOURS.map((c) => (
                            <span
                                key={c}
                                onClick={() => setColor(c)}
                                role="button"
                                aria-label={c}
                                style={{
                                    display: "inline-block",
                                    width: 24,
                                    height: 24,
                                    borderRadius: 12,
                                    background: c,
                                    cursor: "pointer",
                                    boxShadow: color === c ? `0 0 0 2px white, 0 0 0 4px ${c}` : "none",
                                }}
                            />
                        ))}
                    </Space>
                </Form.Item>

                {showSurveySection && (
                    <>
                        <Title level={5} style={{ marginTop: 8 }}>
                            {intl.formatMessage({
                                id: "segments.editor.surveyTitle",
                                defaultMessage: "Survey criteria",
                            })}
                        </Title>
                        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
                            {intl.formatMessage({
                                id: "segments.editor.surveyHint",
                                defaultMessage:
                                    "Filter by which surveys the responses came from. Combined AND-wise with customer criteria.",
                            })}
                        </Paragraph>
                        {showCohortPicker && (
                            <Form.Item
                                label={intl.formatMessage({ id: "segments.editor.cohorts", defaultMessage: "Cohorts" })}
                            >
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder={intl.formatMessage({
                                        id: "segments.editor.cohortsPlaceholder",
                                        defaultMessage: "Any cohort",
                                    })}
                                    value={selectedCohortIds}
                                    onChange={setSelectedCohortIds}
                                    options={cohortOptions}
                                />
                            </Form.Item>
                        )}
                        {showCountryPicker && (
                            <Form.Item
                                label={intl.formatMessage({ id: "segments.editor.countries", defaultMessage: "Countries" })}
                            >
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder={intl.formatMessage({
                                        id: "segments.editor.countriesPlaceholder",
                                        defaultMessage: "Any country",
                                    })}
                                    value={selectedCountryCodes}
                                    onChange={setSelectedCountryCodes}
                                    options={countries.map((c) => ({ value: c, label: c }))}
                                />
                            </Form.Item>
                        )}
                    </>
                )}

                <Title level={5} style={{ marginTop: 8 }}>
                    {intl.formatMessage({
                        id: "segments.editor.customerTitle",
                        defaultMessage: "Customer criteria",
                    })}
                </Title>
                <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
                    {intl.formatMessage({
                        id: "segments.editor.customerHint",
                        defaultMessage:
                            "Filter by customer attributes (the segmentation fields you've defined).",
                    })}
                </Paragraph>
                <SegmentBuilder
                    fields={fields}
                    criteria={criteria}
                    matchLogic={matchLogic}
                    onCriteriaChange={setCriteria}
                    onMatchLogicChange={setMatchLogic}
                />

                <Form.Item style={{ textAlign: "right", marginTop: 24, marginBottom: 0 }}>
                    <Space>
                        <Button onClick={onClose}>
                            {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
                        </Button>
                        <Button type="primary" htmlType="submit" loading={saving}>
                            {initial
                                ? intl.formatMessage({ id: "common.save", defaultMessage: "Save" })
                                : intl.formatMessage({ id: "common.create", defaultMessage: "Create" })}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default SegmentEditorModal;
